// Orchestratore del pattern manifest+cache — l'"ultimo miglio" che collega i pezzi già costruiti
// e testati singolarmente (manifestDiff.ts, manifestCache.ts, manifestFullCache.ts, l'endpoint
// /api/uscite/manifest e /api/uscite/fetch-records). Vedi CLAUDE.md per il disegno completo.
//
// Due funzioni pubbliche:
// - seedManifestFromGetFlat: da chiamare subito dopo un get-flat riuscito, "fotografa" lo stato
//   (snapshot leggero + dati pieni) come base per i confronti successivi.
// - syncManifestIncremental: il ciclo vero e proprio — manifest fresco, diff contro la cache,
//   fetch mirato solo di add/changed, merge, ricostruzione. Pensato per essere chiamato da 3
//   trigger diversi (timer periodico, avvio/reload, dopo una scrittura riuscita) — nessuno dei
//   tre sa nulla degli altri, tutti passano da qui.
import { usciteService, Uscita } from './api';
import { extractManifestFromUscite, diffManifestTree, ManifestDiffPlan, ManifestEntry } from './manifestDiff';
import { syncManifest } from './manifestCache';
import { mergeAssembledResults, deleteRemovedEntities, buildUsciteFromCache, seedFromUscite } from './manifestFullCache';

function planHasChanges(plan: ManifestDiffPlan): boolean {
  const levels = [plan.uscite, plan.uscloc, plan.usclocloc, plan.uscprod, plan.carico, plan.rifornimento];
  return levels.some((level) => level.added.length > 0 || level.changed.length > 0 || level.removed.length > 0);
}

// Quali mob_usc_id sono davvero coinvolti da questo giro di sync — a qualunque livello
// dell'albero (uscita stessa, o una qualunque delle sue entità figlie). Serve al chiamante per
// decidere se vale la pena ri-notificare la selezione corrente: se l'uscita che l'utente sta
// guardando non è tra queste, non c'è nulla di nuovo da mostrare per lei, e ri-notificarla
// comunque farebbe solo ricostruire inutilmente il layer poligoni sulla mappa (stessi dati, nuovi
// riferimenti oggetto) — vedi il "piccolo refresh mappa" osservato dal vivo il 2026-07-09.
function computeTouchedUscIds(freshEntries: ManifestEntry[], plan: ManifestDiffPlan): Set<number> {
  const touchedRecordIds = new Set<number>();
  const collect = (level: { added: any[]; changed: any[]; removed: any[] }) => {
    [...level.added, ...level.changed, ...level.removed].forEach((e) => touchedRecordIds.add(e.recordId));
  };
  collect(plan.uscite);
  collect(plan.uscloc);
  collect(plan.usclocloc);
  collect(plan.uscprod);
  collect(plan.carico);
  collect(plan.rifornimento);

  const touchedUscIds = new Set<number>();
  for (const entry of freshEntries) {
    if (touchedRecordIds.has(entry.recordId)) {
      touchedUscIds.add(entry.mob_usc_id);
      continue;
    }
    const tree = entry.manifest;
    if (!tree) continue;
    const childRecordIds = [
      ...tree.pwa_uscloc.flatMap((u) => [u.recordId, ...u.pwa_usclocloc.map((v) => v.recordId)]),
      ...tree.pwa_uscprod.map((u) => u.recordId),
      ...tree.pwa_carico.map((u) => u.recordId),
      ...tree.pwa_rifornimento.map((u) => u.recordId),
    ];
    if (childRecordIds.some((id) => touchedRecordIds.has(id))) {
      touchedUscIds.add(entry.mob_usc_id);
    }
  }
  return touchedUscIds;
}

// Da chiamare subito dopo un get-flat riuscito (Uscite.tsx, dove oggi si fa setUsciteData).
// Fire-and-forget dal punto di vista del chiamante: non blocca né altera in alcun modo il
// caricamento principale, un fallimento qui non deve mai impedire di mostrare i dati già
// arrivati da get-flat — per questo l'unico compito del chiamante è avvolgerla in un
// "void seedManifestFromGetFlat(...).catch(...)" invece di un await bloccante.
export async function seedManifestFromGetFlat(usciteData: Uscita[]): Promise<void> {
  const entries = extractManifestFromUscite(usciteData);
  await syncManifest(entries);
  await seedFromUscite(usciteData);
}

export interface IncrementalSyncResult {
  uscite: Uscita[];
  // mob_usc_id davvero coinvolti da questo giro (vedi computeTouchedUscIds) — il chiamante lo usa
  // per decidere se la selezione corrente merita di essere ri-notificata, evitando di far
  // ricostruire inutilmente mappa/widget quando cambia un'uscita diversa da quella in vista.
  touchedUscIds: Set<number>;
}

// Il ciclo incrementale vero e proprio. Restituisce:
// - null se il fetch manifest è fallito (rete, sessione scaduta, ecc.) — il chiamante non fa nulla,
//   riproverà al prossimo trigger; oppure se il manifest è arrivato ma NON è cambiato nulla
//   rispetto all'ultimo snapshot noto — il chiamante non deve toccare lo stato React esistente.
// - { uscite, touchedUscIds } se qualcosa è effettivamente cambiato.
export async function syncManifestIncremental(dataDal: Date, dataAl: Date): Promise<IncrementalSyncResult | null> {
  const manifestRes = await usciteService.fetchUsciteManifest(dataDal, dataAl);
  if (manifestRes.error || !manifestRes.data?.manifest) {
    console.warn('[manifestSync] Fetch manifest fallito, salto questo giro:', manifestRes.error);
    return null;
  }
  const freshEntries: ManifestEntry[] = manifestRes.data.manifest;

  const plan = await syncManifest(freshEntries);
  if (!planHasChanges(plan)) {
    return null;
  }

  const recordIds = {
    uscite: [...plan.uscite.added, ...plan.uscite.changed].map((e) => e.recordId),
    uscloc: [...plan.uscloc.added, ...plan.uscloc.changed].map((e) => e.recordId),
    usclocloc: [...plan.usclocloc.added, ...plan.usclocloc.changed].map((e) => e.recordId),
    uscprod: [...plan.uscprod.added, ...plan.uscprod.changed].map((e) => e.recordId),
    carico: [...plan.carico.added, ...plan.carico.changed].map((e) => e.recordId),
    rifornimento: [...plan.rifornimento.added, ...plan.rifornimento.changed].map((e) => e.recordId),
  };
  const hasAnythingToFetch = Object.values(recordIds).some((ids) => ids.length > 0);

  if (hasAnythingToFetch) {
    const fetchRes = await usciteService.fetchUsciteRecords(dataDal, dataAl, recordIds);
    if (fetchRes.error || !fetchRes.data) {
      console.warn('[manifestSync] Fetch mirato fallito, salto questo giro:', fetchRes.error);
      return null;
    }
    await mergeAssembledResults(fetchRes.data);
  }

  await deleteRemovedEntities(plan);

  const uscite = await buildUsciteFromCache(freshEntries);
  const touchedUscIds = computeTouchedUscIds(freshEntries, plan);
  return { uscite, touchedUscIds };
}

// Riesporta diffManifestTree/ManifestDiffPlan per comodità di chi vuole ispezionare il piano
// senza reimportare da manifestDiff.ts direttamente.
export { diffManifestTree };
export type { ManifestDiffPlan };
